import { useCallback, useState } from 'react';
import { Box, Slider, SliderProps } from '@mui/material';

type Props = {
  disabled?: boolean;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
  'aria-label'?: string;
} & Pick<SliderProps, 'orientation' | 'sx'>;

const SpringSlider = ({
  disabled = false,
  min = -100,
  max = 100,
  onChange,
  orientation = 'vertical',
  'aria-label': ariaLabel = 'Spring slider',
  sx,
}: Props) => {
  const [value, setValue] = useState(0);

  const handleChange = useCallback(
    (_event: Event, newValue: number | number[]) => {
      if (disabled) return;
      const v = newValue as number;
      setValue(v);
      onChange(v);
    },
    [disabled, onChange],
  );

  const handleCommit = useCallback(() => {
    setValue(0);
    onChange(0);
  }, [onChange]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 130,
        backgroundColor: 'rgb(39, 39, 46)',
        p: 1,
        borderRadius: 1,
        ...sx,
      }}
    >
      <Slider
        aria-label={ariaLabel}
        orientation={orientation}
        disabled={disabled}
        value={value}
        min={min}
        max={max}
        onChange={handleChange}
        onChangeCommitted={handleCommit}
        getAriaValueText={(v) => `${ariaLabel} ${v}`}
        valueLabelDisplay="off"
        track={false}
        sx={{
          // Thumb = horizontal line
          '& .MuiSlider-thumb': {
            width: 28, // line length
            height: 4, // line thickness
            borderRadius: 1, // slight rounding (or 0 for sharp)
            boxShadow: 'none',
            border: 0,

            '&:hover, &.Mui-focusVisible, &.Mui-active': {
              boxShadow: '#eef0ff86 0px 0px 0px 6px',
            },

            // remove default pseudo-ring
            '&::before': {
              display: 'none',
            },
          },

          // optional: make rail/track thinner for vertical slider
          '& .MuiSlider-rail, & .MuiSlider-track': {
            width: 4,
            border: 'none',
          },

          ...sx,
        }}
      />
    </Box>
  );
};

export default SpringSlider;
